/**
 * LocaleSelectModal — first-visit language picker.
 *
 * Replaces the pre-2026-05-11 silent server-side redirect to English when
 * the visitor's browser/market suggested it. Customer feedback after
 * cutover: explicit choice is preferable to a heuristic guess, especially
 * for bilingual visitors and customers shipping to a different country
 * than they're browsing from.
 *
 * Render gate (client-only, audit 2026-05-11 hydration fix)
 * ----------------------------------------------------------
 * Originally this was server-gated via the `eql_locale_chosen` cookie
 * being absent — when missing, the server rendered the modal into the
 * SSR HTML. That worked for first visits but caused React #418 hydration
 * mismatches on cache-served pages: `/collections` (s-maxage=600) and
 * PDPs (s-maxage=300) public-cache their HTML, so a cached page with
 * the modal could be served to a returning visitor whose cookie says
 * they already chose. Client hydration would see the cookie and try to
 * skip the modal — mismatch with the cached HTML that has it.
 *
 * Now the modal is client-only (same pattern as `CookieConsent`): SSR
 * renders `null`, CSR initial renders `null`, then a `useEffect` reads
 * the cookie and shows the modal if no choice has been made. One frame
 * of flash-free state precedes the modal, which is the cost of
 * cache-safe rendering.
 *
 * Cookie writes (`eql_locale` + `eql_locale_chosen`) are written on
 * button-click here AND by the in-header language switcher, so existing
 * customers who already picked a language via the header never see the
 * modal.
 *
 * Aesthetic
 * ---------
 * Matches `ExitIntentPopup` (overlay + centered card + animations) and
 * `CookieConsent` (button treatment + client-side render gate).
 * Bilingual labels in the modal itself so the customer can act
 * regardless of which locale the underlying page rendered in.
 *
 * No close button — the choice is mandatory. Same model as the cookie
 * banner (Accept/Reject only). The modal is small and the choice is
 * binary, so this isn't friction-heavy.
 */
import { useEffect, useRef, useState, useId } from "react";
import type { Locale } from "~/utils/locale";
import { useFocusTrap } from "~/hooks/useFocusTrap";

interface Props {
  /**
   * Server-detected suggested locale (from market language +
   * Accept-Language header q-weights). Pre-highlighted as the primary
   * call-to-action so the typical visitor confirms with one click.
   */
  suggestedLocale: Locale;
}

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year
const CHOSEN_COOKIE_RE = /(?:^|;\s*)eql_locale_chosen=true/;

function hasChosenLocale(): boolean {
  if (typeof document === "undefined") return true; // SSR: assume yes, modal stays null
  return CHOSEN_COOKIE_RE.test(document.cookie);
}

function writeCookies(locale: Locale) {
  if (typeof document === "undefined") return;
  const isHttps =
    typeof window !== "undefined" && window.location.protocol === "https:";
  const secure = isHttps ? "; Secure" : "";
  // eql_locale: read on every server request to drive page rendering.
  document.cookie = `eql_locale=${locale}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  // eql_locale_chosen: gate flag. Presence = user has made an explicit
  // choice; the modal stops rendering. Independent of eql_locale so an
  // unrelated cookie-purge of one doesn't strand the gate.
  document.cookie = `eql_locale_chosen=true; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

export function LocaleSelectModal({ suggestedLocale }: Props) {
  // Client-only render gate. `show=false` initially so SSR + CSR-first-
  // render both produce `null` (hydration matches). `useEffect` then
  // reads the actual cookie and reveals the modal if needed.
  const [show, setShow] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!hasChosenLocale()) setShow(true);
  }, []);

  // Focus-trap only active when the modal is actually shown.
  useFocusTrap(dialogRef, show, () => {});

  if (!show) return null;

  const select = (locale: Locale) => {
    writeCookies(locale);
    // Reload so the server re-renders the page in the chosen locale and
    // skips the modal on the way back. window.location.reload preserves
    // the current URL + query string + hash.
    if (typeof window !== "undefined") window.location.reload();
  };

  return (
    <div
      className="fixed inset-0 z-[125] flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
      style={{ animation: "localeFadeIn 0.3s ease-out" }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `@keyframes localeFadeIn{from{opacity:0}to{opacity:1}}@keyframes localeScaleIn{from{opacity:0;transform:scale(0.96)}to{opacity:1;transform:scale(1)}}`,
        }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white w-[92vw] max-w-[440px] mx-4 text-center shadow-[0_32px_80px_-12px_rgba(0,0,0,0.2)]"
        style={{ animation: "localeScaleIn 0.35s cubic-bezier(0.16,1,0.3,1)" }}
      >
        <div className="px-10 py-12 sm:px-12 sm:py-14">
          <p className="font-brand text-[12px] font-bold uppercase tracking-normal text-black mb-3">
            DIOLO
          </p>
          <h2
            id={titleId}
            className="font-display text-[20px] font-medium tracking-[-0.02em] text-black mb-3"
          >
            Select your language
          </h2>
          <p className="text-[12px] text-luxury-stone leading-relaxed mb-8">
            Vui lòng chọn ngôn ngữ ưu tiên của bạn.
            <br />
            Choose your preferred language to continue.
          </p>

          <div className="space-y-3">
            {/* Vietnamese button */}
            <button
              type="button"
              onClick={() => select("vi")}
              className={`w-full h-12 text-xs uppercase tracking-[0.15em] transition-colors relative ${
                suggestedLocale === "vi"
                  ? "bg-black text-white hover:bg-luxury-charcoal"
                  : "bg-white text-black border border-[#e0e0e0] hover:border-black"
              }`}
              aria-label="Tiếng Việt"
            >
              Tiếng Việt
              {suggestedLocale === "vi" && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-medium opacity-70 tracking-[0.1em]">
                  Đề xuất
                </span>
              )}
            </button>

            {/* English button */}
            <button
              type="button"
              onClick={() => select("en")}
              className={`w-full h-12 text-xs uppercase tracking-[0.15em] transition-colors relative ${
                suggestedLocale === "en"
                  ? "bg-black text-white hover:bg-luxury-charcoal"
                  : "bg-white text-black border border-[#e0e0e0] hover:border-black"
              }`}
              aria-label="English"
            >
              English
              {suggestedLocale === "en" && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-medium opacity-70 tracking-[0.1em]">
                  Recommended
                </span>
              )}
            </button>
          </div>

          <p className="text-[10px] text-luxury-stone/70 mt-6 leading-relaxed">
            You can change this anytime from the menu.
            <br />
            Bạn có thể thay đổi bất kỳ lúc nào ở menu.
          </p>
        </div>
      </div>
    </div>
  );
}
